import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { ProgressEntryValueDraft } from '@/api/progress/progressApi.types';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Textarea } from '@/components/ui/textarea';
import { WRITABLE_PROGRESS_METRICS } from '@/constants/progressMetrics';
import { useRecordProgressEntriesMutation } from '@/hooks/useProgressMutations';
import { formatDateToIsoDateOnly } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface RecordProgressModalProps {
  patientId: string;
  onClose: () => void;
}

function unitLabelKey(unitCode: string | null): string {
  return unitCode === null ? 'unit.rating' : 'unit.' + unitCode;
}

function resolveFieldValue(valuesByMetric: Record<string, string>, metricCode: string): string {
  const value = valuesByMetric[metricCode];
  return value === undefined ? '' : value;
}

export function RecordProgressModal({ patientId, onClose }: RecordProgressModalProps) {
  const { t } = useTranslation(['progress', 'common']);
  const recordMutation = useRecordProgressEntriesMutation();

  const [recordedOn, setRecordedOn] = useState(formatDateToIsoDateOnly(new Date()));
  const [ownerFeedback, setOwnerFeedback] = useState('');
  const [valuesByMetric, setValuesByMetric] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  function updateValue(metricCode: string, rawValue: string) {
    setValuesByMetric(function mergeValues(previous) {
      return { ...previous, [metricCode]: rawValue };
    });
  }

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setFormError(null);

    const entries: ProgressEntryValueDraft[] = [];
    for (const definition of WRITABLE_PROGRESS_METRICS) {
      const rawValue = valuesByMetric[definition.code];
      if (rawValue === undefined || rawValue.trim().length === 0) {
        continue;
      }
      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) {
        continue;
      }
      const isBelowMinimum = numericValue < definition.minimumValue;
      const isAboveMaximum =
        definition.maximumValue !== null && numericValue > definition.maximumValue;
      if (isBelowMinimum || isAboveMaximum) {
        setFormError(
          t('modal.outOfRangeError', {
            minimum: definition.minimumValue,
            maximum: definition.maximumValue === null ? '∞' : definition.maximumValue,
          }) +
            ' (' +
            t('metric.' + definition.code) +
            ')'
        );
        return;
      }
      entries.push({ metricCode: definition.code, value: numericValue });
    }

    if (entries.length === 0) {
      setFormError(t('modal.noValuesError'));
      return;
    }

    recordMutation.mutate(
      {
        patientId,
        draft: {
          recordedOn,
          entries,
          ownerFeedback: ownerFeedback.trim().length === 0 ? null : ownerFeedback.trim(),
          sessionNoteId: null,
        },
      },
      {
        onSuccess: function handleSuccess() {
          showSuccessNotification(t('toasts.recorded'));
          onClose();
        },
        onError: function handleError(mutationError) {
          const backendMessage = resolveApiErrorMessage(mutationError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('toasts.recordError')
          );
        },
      }
    );
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submit}
        className="w-full max-w-xl bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">{t('modal.title')}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('common:actions.close')}
            onClick={onClose}
          >
            <Icon name="X" className="text-zoomuted" />
          </Button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <p className="text-sm text-zoomuted">{t('modal.description')}</p>

          <div className="space-y-1.5">
            <Label htmlFor="progress-recorded-on">{t('modal.dateLabel')}</Label>
            <Input
              id="progress-recorded-on"
              type="date"
              value={recordedOn}
              onChange={function updateRecordedOn(changeEvent) {
                setRecordedOn(changeEvent.target.value);
              }}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {WRITABLE_PROGRESS_METRICS.map(function renderMetricField(definition) {
              const fieldId = 'progress-metric-' + definition.code;
              return (
                <div key={definition.code} className="space-y-1.5">
                  <Label htmlFor={fieldId}>
                    {t('metric.' + definition.code)} ({t(unitLabelKey(definition.unitCode))})
                  </Label>
                  <Input
                    id={fieldId}
                    type="number"
                    step="0.1"
                    min={definition.minimumValue}
                    max={definition.maximumValue === null ? undefined : definition.maximumValue}
                    value={resolveFieldValue(valuesByMetric, definition.code)}
                    onChange={function updateMetricValue(changeEvent) {
                      updateValue(definition.code, changeEvent.target.value);
                    }}
                  />
                </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="progress-owner-feedback">{t('modal.ownerFeedbackLabel')}</Label>
            <Textarea
              id="progress-owner-feedback"
              rows={3}
              placeholder={t('modal.ownerFeedbackPlaceholder')}
              value={ownerFeedback}
              onChange={function updateOwnerFeedback(changeEvent) {
                setOwnerFeedback(changeEvent.target.value);
              }}
            />
          </div>

          {formError === null ? null : (
            <p className="text-xs font-medium text-zooerrortext">{formError}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('modal.cancel')}
          </Button>
          <Button type="submit" isLoading={recordMutation.isPending}>
            {recordMutation.isPending ? t('section.recording') : t('modal.submit')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}
